import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axiosInstance from '@/services/api/axios';

interface Deposit {
  id: string;
  trader_id: string;
  balance_id?: string;
  created_at: string;
  trader_name?: string;
  asset: string;
  network: string;
  transferred_amount?: string;
  provider_amount?: string;
  provider_fee?: string;
  network_fee?: string;
  credited_amount?: string;
  insurance_credited_amount_usdt?: string;
  exchange_rate?: string;
  currency?: string;
  status: string;
  tx_hash?: string;
  failure_reason?: string;
  review_comment?: string;
  reviewed_at?: string;
  decision_window_started_at?: string;
}

interface TraderBalance {
  id: string;
  trader_id: string;
  currency?: { iso_code?: string };
}

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'credited') return 'success';
  if (status === 'manual_review') return 'warning';
  if (status === 'rejected') return 'error';
  return 'default';
};

const isWithinChangeWindow = (row: Deposit) => {
  if (!row.decision_window_started_at) return false;
  const startedAt = new Date(row.decision_window_started_at).getTime();
  const elapsed = Date.now() - startedAt;
  return Number.isFinite(startedAt) && elapsed >= 0 && elapsed <= 24 * 60 * 60 * 1000;
};

const tronTransactionUrl = (txHash: string) =>
  `https://tronscan.org/#/transaction/${encodeURIComponent(txHash)}`;

export default function Deposits() {
  const [items, setItems] = useState<Deposit[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [balances, setBalances] = useState<TraderBalance[]>([]);
  const [approvalRow, setApprovalRow] = useState<Deposit | null>(null);
  const [selectedBalanceId, setSelectedBalanceId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assetFilter, setAssetFilter] = useState('');
  const assets = Array.from(new Set(items.map((item) => item.asset))).sort();
  const filteredItems = items.filter((item) => {
    const needle = search.trim().toLowerCase();
    return (!needle || [item.id, item.trader_id, item.trader_name, item.tx_hash].some((value) => value?.toLowerCase().includes(needle))) && (!statusFilter || item.status === statusFilter) && (!assetFilter || item.asset === assetFilter);
  });

  useEffect(() => {
    const timer = window.setInterval(() => setItems((current) => [...current]), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [depositsResponse, balancesResponse] = await Promise.all([
        axiosInstance.get('/deposits', { params: { page_size: 200, search: search || undefined, status: statusFilter || undefined, asset: assetFilter || undefined } }),
        axiosInstance.get('/traders/balances/'),
      ]);
      setItems(depositsResponse.data.items);
      setBalances(balancesResponse.data);
    } catch (requestError) {
      setError(axios.isAxiosError(requestError) ? requestError.response?.data?.detail || requestError.message : 'Не удалось загрузить пополнения');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, assetFilter]);

  useEffect(() => { const timer = window.setTimeout(() => void load(), 300); return () => window.clearTimeout(timer); }, [load]);

  const openApproval = (row: Deposit) => {
    setApprovalRow(row);
    setSelectedBalanceId(row.balance_id || '');
  };

  const approve = async () => {
    if (!approvalRow || !selectedBalanceId) return;
    const row = approvalRow;
    setProcessingId(row.id);
    try {
      await axiosInstance.post(`/deposits/${row.id}/approve`, {
        balance_id: selectedBalanceId,
        comment: null,
      });
      setApprovalRow(null);
      await load();
    } catch (requestError) {
      setError(axios.isAxiosError(requestError) ? requestError.response?.data?.detail || requestError.message : 'Не удалось разрешить пополнение');
    } finally {
      setProcessingId(null);
    }
  };

  const reject = async (row: Deposit) => {
    const prompt = row.status === 'credited'
      ? `Рабочий баланс будет уменьшен на ${value(row.credited_amount)} ${row.currency || ''}, страховой — на ${value(row.insurance_credited_amount_usdt)} USDT. Укажите причину изменения статуса:`
      : 'Укажите причину отказа';
    const comment = window.prompt(prompt);
    if (!comment?.trim()) return;
    setProcessingId(row.id);
    try {
      await axiosInstance.post(`/deposits/${row.id}/reject`, { comment: comment.trim() });
      await load();
    } catch (requestError) {
      setError(axios.isAxiosError(requestError) ? requestError.response?.data?.detail || requestError.message : 'Не удалось отклонить пополнение');
    } finally {
      setProcessingId(null);
    }
  };

  const value = (amount?: string) => amount ?? '—';

  return <Box>
    <Box display="flex" justifyContent="space-between" mb={3}>
      <Typography variant="h4" fontWeight={600}>Пополнения</Typography>
      <Button variant="outlined" onClick={() => void load()} disabled={loading}>Обновить</Button>
    </Box>
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}><Stack direction={{ xs: 'column', md: 'row' }} spacing={2}><TextField fullWidth label="Поиск по трейдеру, ID или tx hash" value={search} onChange={(event) => setSearch(event.target.value)} /><FormControl sx={{ minWidth: 200 }}><InputLabel>Статус</InputLabel><Select value={statusFilter} label="Статус" onChange={(event) => setStatusFilter(event.target.value)}><MenuItem value="">Все</MenuItem>{Array.from(new Set(items.map((item) => item.status))).map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</Select></FormControl><FormControl sx={{ minWidth: 160 }}><InputLabel>Актив</InputLabel><Select value={assetFilter} label="Актив" onChange={(event) => setAssetFilter(event.target.value)}><MenuItem value="">Все</MenuItem>{assets.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</Select></FormControl><Button onClick={() => { setSearch(''); setStatusFilter(''); setAssetFilter(''); }}>Сбросить</Button></Stack></Paper>
    <TableContainer component={Paper}><Table size="small">
      <TableHead><TableRow>
        <TableCell>Дата</TableCell><TableCell>Трейдер</TableCell><TableCell>Актив / сеть</TableCell>
        <TableCell align="right">Переведено</TableCell>
        <TableCell align="right">Получил сервис</TableCell><TableCell align="right">Комиссия сервиса / сети</TableCell>
        <TableCell align="right">Курс USDT/фиат</TableCell><TableCell align="right">В страховой</TableCell><TableCell align="right">В рабочий</TableCell>
        <TableCell>Статус</TableCell><TableCell>Tx hash</TableCell><TableCell>Действия</TableCell>
      </TableRow></TableHead>
      <TableBody>{filteredItems.map((row) => <TableRow key={row.id} hover>
        <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
        <TableCell>{row.trader_name || row.id}</TableCell><TableCell>{row.asset} / {row.network}</TableCell>
        <TableCell align="right">{value(row.transferred_amount)} USDT</TableCell>
        <TableCell align="right">{value(row.provider_amount)} USDT</TableCell>
        <TableCell align="right">{value(row.provider_fee)} / {value(row.network_fee)} USDT</TableCell>
        <TableCell align="right">{value(row.exchange_rate)}</TableCell>
        <TableCell align="right">{value(row.insurance_credited_amount_usdt)} USDT</TableCell>
        <TableCell align="right">{value(row.credited_amount)} {row.currency || ''}</TableCell>
        <TableCell>
          <Chip size="small" label={row.status} color={statusColor(row.status)} title={row.failure_reason || row.review_comment} />
        </TableCell>
        <TableCell sx={{ maxWidth: 180 }}>
          {row.tx_hash ? (
            <Button
              component="a"
              href={tronTransactionUrl(row.tx_hash)}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              title={row.tx_hash}
              sx={{
                maxWidth: '100%',
                minWidth: 0,
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontFamily: 'monospace',
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.tx_hash}
              </Box>
            </Button>
          ) : '—'}
        </TableCell>
        <TableCell>
          {(row.status === 'manual_review' || (row.status === 'rejected' && isWithinChangeWindow(row))) && <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" color="success" disabled={processingId === row.id} onClick={() => openApproval(row)}>Разрешить</Button>
            {row.status === 'manual_review' &&
            <Button size="small" variant="outlined" color="error" disabled={processingId === row.id} onClick={() => void reject(row)}>Отклонить</Button>
            }
          </Stack>}
          {row.status === 'credited' && isWithinChangeWindow(row) &&
            <Button size="small" variant="outlined" color="error" disabled={processingId === row.id} onClick={() => void reject(row)}>Отклонить</Button>
          }
        </TableCell>
      </TableRow>)}</TableBody>
    </Table></TableContainer>
    <Dialog open={Boolean(approvalRow)} onClose={() => setApprovalRow(null)} fullWidth maxWidth="xs">
      <DialogTitle>Подтверждение пополнения</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Переведено: {approvalRow?.transferred_amount || '—'} USDT
        </Typography>
        <FormControl fullWidth disabled={Boolean(approvalRow?.balance_id)}>
          <InputLabel id="deposit-balance-label">Баланс зачисления</InputLabel>
          <Select
            labelId="deposit-balance-label"
            value={selectedBalanceId}
            label="Баланс зачисления"
            onChange={(event) => setSelectedBalanceId(event.target.value)}
          >
            {balances
              .filter((balance) => balance.trader_id === approvalRow?.trader_id)
              .map((balance) => (
                <MenuItem key={balance.id} value={balance.id}>
                  {balance.currency?.iso_code || balance.id}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setApprovalRow(null)}>Отмена</Button>
        <Button
          variant="contained"
          color="success"
          disabled={!selectedBalanceId || processingId === approvalRow?.id}
          onClick={() => void approve()}
        >
          Подтвердить
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
      <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
    </Snackbar>
  </Box>;
}
